import React from 'react';
import { isClientInFlOrNy } from '../util/gearOrderUtil';

/**
 * SCR-7348: Display details about insurance payments and next steps.
 *
 * @param gearOrder The current gear order
 * @param prices {Object[]} The array of price objects to use for price calculations
 * @param doNotQuotePrices {boolean} A flag indicating if prices should not be displayed
 */
export default function InsurancePaymentDisclaimer({ gearOrder, prices, doNotQuotePrices }) {
	let iip;
	const priceLoaded = prices?.every(
		(priceObject) => !!priceObject && !priceObject?.error && !priceObject.placeholder
	);
	const displayPrice = !doNotQuotePrices && priceLoaded;

	// General steps to show when we don't have all prices yet or when doNotQuotePrices is true
	let steps = [
		<span>
			Submit the required <strong>Initial Payment</strong> with your specimen.
		</span>,
		<span>
			After the claim is processed, <strong>a statement will be sent</strong> if there is additional Patient
			Responsibility.
		</span>,
		<span>
			Pay by the date indicated on your billing statement to receive discounts available to many of our customers.
		</span>
	];

	// If we can show prices, update the appropriate steps with price details
	if (displayPrice) {
		iip = 0;
		let maxOOP = 0;

		prices.forEach((priceObject) => {
			let priceObjectIIP = priceObject?.price || 0;
			let priceObjectMaxOOP = Math.max(priceObject?.maxOutOfPocket || 0, priceObjectIIP);

			iip += priceObjectIIP;
			maxOOP += priceObjectMaxOOP;
		});

		maxOOP = Math.max(maxOOP || 0, iip);

		const additionalCost = maxOOP - iip;
		const initialPaymentDisplay = '$' + parseFloat(Math.round(iip * 100) / 100).toFixed(2);
		const additionalCostDisplay = '$' + parseFloat(Math.round(additionalCost * 100) / 100).toFixed(2);

		if (iip > 0) {
			steps[0] = (
				<span>
					Submit the required <strong>Initial Payment</strong> with your specimen,{' '}
					<strong>{initialPaymentDisplay}</strong>.
				</span>
			);
		}

		if (maxOOP > iip) {
			steps[2] = (
				<span>
					The additional out of pocket costs will not be higher than <strong>{additionalCostDisplay}</strong>{' '}
					as long as future balances are paid promptly, by the date indicated on any billing statements.
				</span>
			);
		}
	}

	// SCR-6524: Show a different disclaimer for patients in Florida or New York
	if (isClientInFlOrNy(gearOrder)) {
		return displayPrice ? <FlNyInsurancePaymentDisclaimer iip={iip} /> : null;
	}

	return (
		<div>
			<p>
				<strong>
					When billing insurance, follow these steps to qualify for the lowest out-of-pocket cost:
				</strong>
			</p>
			<ol>
				{steps.map((step, index) => (
					<li key={index}>{step}</li>
				))}
			</ol>
		</div>
	);
}

/**
 * SCR-6524: Show a different disclaimer for patients in Florida or New York
 *
 * @param iip {number} The initial payment amount
 */
function FlNyInsurancePaymentDisclaimer({ iip }) {
	// Don't display anything if the price is not available
	if (iip == null) {
		return null;
	}

	return (
		<div>
			<p>
				<strong>Bill Insurance with Patient Payment</strong>
			</p>
			<p>
				Based on your chosen panel and add-ons, your <strong>Initial Payment</strong> due with your sample
				submission is <strong>${parseFloat(Math.round(iip * 100) / 100).toFixed(2)}</strong>.
			</p>
			<p>
				Please include this amount with your specimens or provide payment information online. If your insurance
				provider indicates you owe more based on your policy, we will bill you for the amount they indicate. If
				you paid the Initial Payment amount above, we will apply credits on any amounts denied by your
				insurance.
			</p>
			<p>
				In our experience, it is likely that your insurance will assign some additional costs. If you have a
				high deductible, those costs could be substantially higher than your amount due today. If you receive a
				bill and cannot pay the amount due, please contact us to discuss flexible payment options.
			</p>
			<p>
				Alternatively, you may use our No Insurance Billing Option which provides you with the total cost today.
				Patients who use this option receive our discounted patient pricing and can file a claim to their
				insurance for reimbursement.
			</p>
			<p>
				This price is based on your selections. Costs or initial payment amount may vary with different test
				options, payment methods, or insurance providers.
			</p>
		</div>
	);
}
